import { useEffect, useState } from 'react';
import { Box, Center, Container, Heading, Spinner, Text } from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import PublicContent from '@/components/content-list/public-content';
import WorkspaceShell from '@/components/layout/workspace-shell';
import { baseApi } from '@/utils/api';

type Meta = {
  workspaceName?: string;
  workspaceLogo?: string;
  workspaceBannerImage?: string;
  contentTypeName?: string;
};

type Status = 'loading' | 'ready' | 'not-found';

// A /public/content-categories/:ref link whose reference id isn't a content
// type is almost always an older collection or collection-type share link —
// before each kind got its own /public route, they were distributed on this
// path. Probe the collection endpoints and return the correct route so the old
// link still resolves. Returns null when the ref matches nothing (dead link).
async function resolveLegacyPublicRoute(referenceId: string): Promise<string | null> {
  try {
    const res = await baseApi.get(`public/collections/reference/${referenceId}`);
    if (res.data) return `/public/collections/${referenceId}`;
  } catch {
    // not a collection — fall through
  }
  try {
    const res = await baseApi.get(`/public/collection-types/${referenceId}`);
    if (res.data) return `/public/collection-types/${referenceId}`;
  } catch {
    // not a collection type — fall through
  }
  return null;
}

export default function PublicContentList() {
  const { referenceId } = useParams<{ referenceId: string }>();
  const navigate = useNavigate();
  const [meta, setMeta] = useState<Meta>({});
  const [status, setStatus] = useState<Status>('loading');

  useEffect(() => {
    if (!referenceId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await baseApi.post('/public/contents/search', {
          content_type_reference_id: referenceId,
          q: '',
          page: 1,
          page_size: 1,
          sort: ['created_at:desc'],
          filters: [{ key: 'status', val: 'published' }],
          select_list_filters: [],
          facet_fields: [],
        });
        if (cancelled) return;
        const data = response.data?.data;
        setMeta({
          workspaceName: data?.Workspace?.name,
          workspaceLogo: data?.Workspace?.logo,
          workspaceBannerImage: data?.Workspace?.banner_image,
          contentTypeName: data?.ContentType?.name,
        });
        setStatus('ready');
      } catch (err: any) {
        if (cancelled) return;
        // Not a content type: a stale collection/collection-type link may have
        // landed here. Detect the real kind and redirect; otherwise it's a dead
        // link, so show a friendly not-found state instead of a broken page.
        if (err?.response?.status === 404) {
          const target = await resolveLegacyPublicRoute(referenceId);
          if (cancelled) return;
          if (target) {
            navigate(target, { replace: true });
            return;
          }
          setStatus('not-found');
          return;
        }
        // Branding is non-critical chrome for any other failure — let the
        // content list render and surface its own errors as before.
        setStatus('ready');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [referenceId, navigate]);

  return (
    <WorkspaceShell
      workspaceName={meta.workspaceName}
      workspaceLogo={meta.workspaceLogo}
      workspaceBannerImage={meta.workspaceBannerImage}
    >
      <Container maxW='5xl' px={{ base: 4, md: 6 }} mt={{ base: -6, md: -10 }} pb={10}>
        <Box bg='bg.panel' borderRadius='lg' shadow={{ base: 'sm', md: 'lg' }} p={{ base: 5, md: 8 }}>
          {status === 'loading' && (
            <Center py={16}>
              <Spinner size='xl' />
            </Center>
          )}

          {status === 'not-found' && (
            <Center py={16} flexDirection='column' gap={3} textAlign='center'>
              <Heading as='h1' fontSize={{ base: 'xl', md: '2xl' }} fontWeight='bold'>
                This link is no longer available
              </Heading>
              <Text color='fg.muted' maxW='md'>
                The content you&apos;re looking for may have been moved or deleted.
              </Text>
            </Center>
          )}

          {status === 'ready' && (
            <>
              {meta.contentTypeName && (
                <Heading
                  as='h1'
                  fontSize={{ base: '2xl', md: '3xl' }}
                  fontWeight='bold'
                  letterSpacing='-0.01em'
                  mb={5}
                >
                  {meta.contentTypeName}
                </Heading>
              )}
              {referenceId && <PublicContent referenceId={referenceId} hasHeader={false} />}
            </>
          )}
        </Box>
      </Container>
    </WorkspaceShell>
  );
}
